import Fuse from "fuse.js";
import { aramaMetni } from "./katalog";

/**
 * Yazım hatasına dayanıklı ürün araması.
 *   1) Tam eşleşme: her kelime ürün adı + markada geçmeli (en isabetli).
 *   2) Sonuç yoksa kelimeleri katalogdaki en yakın kelimeye düzelt ("silkon" → "silikon") ve tekrar ara.
 *   3) Yine yoksa ürün adlarında bulanık arama.
 * Dönüş: { sonuclar, duzeltme } — duzeltme doluysa "Bunu mu demek istediniz?" olarak gösterilir.
 */

const onbellek = new WeakMap(); // ürün listesi -> dizin (her aramada yeniden kurulmasın)

export const aramaDizini = (urunler) => {
  if (onbellek.has(urunler)) return onbellek.get(urunler);
  const kayitlar = urunler.map((u) => ({ u, m: aramaMetni(`${u.ad} ${u.marka}`) }));
  // Kelime dağarcığı (sıklığıyla): düzeltmede sık geçen kelime tercih edilir
  const siklik = new Map();
  for (const k of kayitlar)
    for (const w of k.m.split(/[^a-z0-9]+/)) if (w.length >= 3 && !/^\d+$/.test(w)) siklik.set(w, (siklik.get(w) || 0) + 1);
  const kelimeler = [...siklik.entries()].map(([w, n]) => ({ w, n }));
  const dizin = {
    kayitlar,
    kelimeFuse: new Fuse(kelimeler, { keys: ["w"], threshold: 0.34, includeScore: true, ignoreLocation: true, minMatchCharLength: 3 }),
    urunFuse: new Fuse(kayitlar, { keys: ["m"], threshold: 0.38, ignoreLocation: true, includeScore: true, minMatchCharLength: 3 }),
    sozluk: siklik,
  };
  onbellek.set(urunler, dizin);
  return dizin;
};

const tamEslesme = (dizin, kelimeler, q) => {
  const sonuc = [];
  for (const x of dizin.kayitlar) {
    if (!kelimeler.every((k) => x.m.includes(k))) continue;
    const puan = (x.m.startsWith(q) ? 3 : 0) + (x.m.includes(` ${kelimeler[0]}`) ? 1 : 0) - x.m.length / 400;
    sonuc.push({ u: x.u, puan });
  }
  return sonuc.sort((a, b) => b.puan - a.puan).map((x) => x.u);
};

/** İki kelime arasındaki düzenleme mesafesi (harf ekleme/silme/değiştirme ve yer değiştirme) */
const mesafe = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) {
      const m = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + m);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  return d[a.length][b.length];
};

/** Bir kelimeyi sözlükteki en yakın kelimeye düzeltir; zaten sözlükteyse ya da yakın kelime yoksa aynen döner */
const kelimeDuzelt = (dizin, w) => {
  if (w.length < 3 || /^\d+$/.test(w) || dizin.sozluk.has(w)) return w;
  const adaylar = dizin.kelimeFuse.search(w, { limit: 30 });
  // Sorguyu zaten içeren kelimeler ("matkab" → "matkabı", "fili" → "profili") tam eşleşmede bulunur;
  // düzeltme, onların dışında kalan asıl kastedilen kelimeyi ("matkap", "filli") bulmalı
  const havuz = adaylar.filter((a) => !a.item.w.includes(w));
  if (!havuz.length) return w;
  // Sıralama: düzenleme mesafesi → ilk harf aynı mı (yazım hatalarında nadiren yanlış) → sıklık
  const sirali = havuz
    .map((a) => ({ w: a.item.w, n: a.item.n, m: mesafe(w, a.item.w), ilk: a.item.w[0] === w[0] ? 0 : 1 }))
    .sort((a, b) => a.m - b.m || a.ilk - b.ilk || b.n - a.n);
  // Çok uzak düzeltme yapılmaz (kısa kelimede 1, uzun kelimede 2 harf farkına kadar)
  return sirali[0].m <= (w.length <= 5 ? 1 : 2) ? sirali[0].w : w;
};

export const akilliAra = (urunler, sorgu) => {
  const q = aramaMetni(sorgu);
  if (q.length < 2) return { sonuclar: [], duzeltme: null };
  const dizin = aramaDizini(urunler);
  const kelimeler = q.split(/\s+/).filter(Boolean);

  const tam = tamEslesme(dizin, kelimeler, q);
  const duzeltilmis = kelimeler.map((w) => kelimeDuzelt(dizin, w));
  const yeniQ = duzeltilmis.join(" ");
  const ikinci = yeniQ !== q ? tamEslesme(dizin, duzeltilmis, yeniQ) : [];

  // Tam eşleşme varsa o kullanılır; ancak çok az sonuç verip düzeltilmiş sorgu çok daha fazlasını buluyorsa
  // (ör. "matkab" yalnızca "matkabı"nı bulur, "matkap" onlarcasını) düzeltme önerilir.
  if (tam.length && !(ikinci.length >= 5 && ikinci.length > tam.length * 3)) return { sonuclar: tam, duzeltme: null };
  if (ikinci.length) return { sonuclar: ikinci, duzeltme: yeniQ };

  const bulanik = dizin.urunFuse.search(q, { limit: 60 }).map((r) => r.item.u);
  return { sonuclar: bulanik, duzeltme: bulanik.length && yeniQ !== q ? yeniQ : null, bulanik: bulanik.length > 0 };
};
